import { renderNavbar } from '../components/navbar.js';
import { supabase, isSupabaseReady } from '../services/supabase.js';
import { escapeHtml } from '../utils/html.js';
import { activeCompetitionLabel, dataProviderLabel, formatKickoff, teamFlag } from '../utils/competitions.js';
import { formatNumber, tr } from '../services/i18n.js';

export async function renderCompetitions(container) {
  container.innerHTML = `
    <div class="page p-16" style="padding-bottom:100px;">
      <div class="app-header">
        <div class="brand">
          <div class="brand-mark">FV</div>
          <div>
            <p class="brand-title">${tr('Compétitions', 'Competitions')}</p>
            <p class="brand-subtitle">${tr('Matchs, contextes et débriefs associés', 'Matches, context, and linked debriefs')}</p>
          </div>
        </div>
        <a class="btn-secondary" href="#/upload">${tr('Analyser', 'Analyze')}</a>
      </div>
      <div id="competitions-content">
        <div class="glass-card skeleton" style="height:120px;margin-bottom:16px;"></div>
        <div class="glass-card skeleton" style="height:180px;"></div>
      </div>
    </div>
  `;
  renderNavbar(container);

  const content = container.querySelector('#competitions-content');
  if (!isSupabaseReady()) {
    content.innerHTML = `<div class="glass-card"><p class="text-muted">${tr('Supabase non configuré.', 'Supabase is not configured.')}</p></div>`;
    return;
  }

  try {
    const { data: competitions, error } = await supabase
      .from('competitions')
      .select('*, matches(*)')
      .eq('is_active', true)
      .order('starts_at', { ascending: true });
    if (error) throw error;

    if (!competitions?.length) {
      content.innerHTML = `
        <div class="glass-card">
          <p style="font-weight:700;margin-bottom:8px;">${tr('Aucune compétition active', 'No active competition')}</p>
          <p class="text-muted" style="margin:0;">${tr('Applique la migration Supabase gamification pour charger CDM 2026 et les matchs.', 'Apply the Supabase gamification migration to load the 2026 World Cup and matches.')}</p>
        </div>`;
      return;
    }

    content.innerHTML = `
      <div class="flex-col gap-16">
        ${competitions.map(renderCompetition).join('')}
      </div>
    `;
  } catch (e) {
    content.innerHTML = `
      <div class="glass-card">
        <p style="font-weight:700;margin-bottom:8px;">${tr('Compétitions indisponibles', 'Competitions unavailable')}</p>
        <p class="text-muted" style="margin:0;">${tr('Applique la migration Supabase pour activer les compétitions et les matchs.', 'Apply the Supabase migration to enable competitions and matches.')}</p>
      </div>`;
  }
}

function renderCompetition(c) {
  const matches = Array.isArray(c.matches) ? [...c.matches].sort((a, b) => new Date(a.kickoff_at || 0) - new Date(b.kickoff_at || 0)) : [];
  const sourceSummary = competitionSourceSummary(c, matches);
  return `
    <section class="glass-card">
      <p class="eyebrow">${escapeHtml(c.scope || 'competition')}</p>
      <h2 class="heading-2" style="margin-bottom:6px;">${escapeHtml(activeCompetitionLabel(c))}</h2>
      <p class="text-muted" style="margin:0 0 14px;">${escapeHtml(formatKickoff(c.starts_at))} → ${escapeHtml(formatKickoff(c.ends_at))} · ${formatNumber(matches.length, { maximumFractionDigits: 0 })} ${tr('match', 'match')}${matches.length > 1 ? 's' : ''}</p>
      ${matches.length ? `
        <div class="flex-col gap-12">
          ${matches.map(m => `
            <div class="match-row-card">
              <div class="match-row-head">
                <div class="match-teams">
                  ${renderMatchTeam(m.home_team, m.source_payload?.homeCode)}
                  <span class="match-versus">vs</span>
                  ${renderMatchTeam(m.away_team, m.source_payload?.awayCode)}
                </div>
                <span class="text-muted" style="font-size:12px;">${escapeHtml(formatKickoff(m.kickoff_at))}</span>
              </div>
              ${m.stage ? `<p class="text-muted" style="font-size:12px;margin:6px 0 0;">${escapeHtml(m.stage)}</p>` : ''}
              ${m.venue ? `<p class="text-muted" style="font-size:12px;margin:4px 0 0;">${escapeHtml(m.venue)}</p>` : ''}
            </div>
          `).join('')}
        </div>
      ` : `<p class="text-muted">${tr('Matchs à importer.', 'Matches to import.')}</p>`}
      <p class="text-muted" style="font-size:12px;margin:14px 0 0;">${escapeHtml(sourceSummary)}</p>
    </section>
  `;
}

function competitionSourceSummary(competition, matches) {
  const providers = new Set();
  if (competition?.source_provider) providers.add(dataProviderLabel(competition.source_provider));
  matches.forEach((match) => {
    if (match?.source_provider) providers.add(dataProviderLabel(match.source_provider));
  });
  const sourceList = [...providers].filter(Boolean).join(', ') || dataProviderLabel('');
  return tr(
    `Sources données: ${sourceList}. Horaires, lieux et statuts à vérifier auprès de l'organisateur officiel avant publication.`,
    `Data sources: ${sourceList}. Times, venues, and statuses should be checked with the official organizer before publication.`
  );
}

function renderMatchTeam(name, code) {
  const label = String(name || '').trim() || tr('Equipe à confirmer', 'Team TBC');
  const flag = teamFlag(label, code);
  return `
    <span class="match-team">
      ${renderMatchTeamFlag(flag)}
      <span>${escapeHtml(label)}</span>
    </span>
  `;
}

function renderMatchTeamFlag(flag) {
  if (flag?.src) {
    const srcset = flag.srcset ? ` srcset="${escapeHtml(flag.srcset)}"` : '';
    return `<img class="match-team-flag" src="${escapeHtml(flag.src)}"${srcset} width="24" height="18" loading="lazy" alt="${escapeHtml(flag.alt)}">`;
  }
  if (flag?.emoji) {
    return `<span class="match-team-flag-emoji" role="img" aria-label="${escapeHtml(flag.alt)}">${escapeHtml(flag.emoji)}</span>`;
  }
  return '';
}
